// Reads the attribution and Meta browser cookies for lead payloads (spec §4.4, §9).
// Identifiers (attribution touches, fbp, fbc) are sent only with granted ad
// consent — ad_consent itself is always sent so the backend can tell "no consent"
// apart from "no attribution". Call in component setup (it uses the consent store).
import {
  ATTRIBUTION_COOKIE,
  decodeAttribution,
  stripClickIdsFromPath,
  type AttributionState,
} from '~/utils/tracking/attributionCookie';
import { readCookie } from '~/utils/tracking/consentRecord';

export const useAttribution = () => {
  const { adConsent } = useCookieConsent();

  const current = (): AttributionState | null =>
    import.meta.client ? decodeAttribution(readCookie(document.cookie, ATTRIBUTION_COOKIE)) : null;

  const leadTrackingFields = () => {
    const cookies = import.meta.client ? document.cookie : '';
    const state = current();
    const granted = adConsent.value === 'granted';
    return {
      attribution: granted
        ? {
            first: state?.first ?? null,
            last: state?.last ?? null,
            last_meta: state?.last_meta ?? null,
          }
        : { first: null, last: null, last_meta: null },
      ad_consent: adConsent.value,
      fbp: granted ? (readCookie(cookies, '_fbp') ?? '') : '',
      fbc: granted ? readCookie(cookies, '_fbc') || state?.last?.fbc || state?.last_meta?.fbc || '' : '',
      // Without granted consent, override the caller's own source_path with one that
      // has click ids stripped (spec I1) — an EU visitor who hasn't answered the
      // consent banner must not have fbclid/gclid/gbraid/wbraid land in storage via
      // source_path. Granted callers get no source_path key at all here, so their
      // own value survives untouched. This only works because every caller spreads
      // ...leadTrackingFields() AFTER building its own source_path in the same
      // object literal — a spread in the other order, or a source_path built after
      // the spread, would silently lose this override.
      ...(granted || !import.meta.client
        ? {}
        : { source_path: stripClickIdsFromPath(`${window.location.pathname}${window.location.search}`).slice(0, 500) }),
    };
  };

  return { current, leadTrackingFields };
};
